import { useRef } from 'react';
import { useStore } from '../app/context.js';
import { Icon } from '../parts/Icon.js';
import { cls } from '../parts/classes.js';
import type { WidgetVals } from '../select/grid.js';
import { mediaEnded, mediaProgress, mediaStopped, togglePlay } from '../state/playback.js';
import { color, icon } from '../tokens.js';
import { useMediaSync } from './Media.js';

/**
 * The song widget of a profile (02 §3.20): play or pause, title, then the artist or the time, and a
 * progress line in the owner's colour. An unavailable song is greyed with "Not available right now".
 * Sound only ever starts from the play button.
 */
export function SongBody({ w }: { readonly w: WidgetVals }) {
  const s = useStore(), v = w.song, src = v?.song?.src ?? '';
  const ref = useRef<HTMLAudioElement | null>(null);
  useMediaSync(ref, w.key, !!v?.playing && !!src, v?.pos ?? 0);
  if (!v) return null;
  return <div style={{ position: 'absolute', left: 12, right: 12, top: 0, bottom: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
    {v.canPlay && src && <audio ref={ref} src={src} preload="none"
      onTimeUpdate={(e) => mediaProgress(s, w.key, e.currentTarget.currentTime, e.currentTarget.duration)}
      onEnded={() => mediaEnded(s, w.key)} onError={() => mediaStopped(s, w.key)} />}
    {v.canPlay && <button type="button" className={cls('ctl', 'pb')} data-v1-play="" aria-label={v.playLabel} onClick={() => togglePlay(s, w.key, v.len, !!src)} style={{ flex: 'none', width: 44, height: 44, padding: 0, border: 'none', borderRadius: '50%', background: color.paper, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon d={v.playing ? icon.pause : icon.play} size={16} fill={color.ink} />
    </button>}
    {v.offIcon && <span aria-hidden="true" style={{ flex: 'none', width: 40, height: 40, borderRadius: '50%', background: color.tileHover, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon d={icon.play} size={16} fill={color.textPlaceholder} />
    </span>}
    <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, pointerEvents: 'none' }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14, color: color.textStrong }}>{v.title}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: v.subColor }}>{v.sub}</span>
      <div style={{ height: 2, borderRadius: 1, background: color.tileHover, overflow: 'hidden', opacity: v.barOp }}><div style={{ height: '100%', width: v.pct, background: v.color }} /></div>
    </div>
  </div>;
}
